import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Mail, Phone, MapPin, Send, User, Building2, Loader2, AlertCircle, Check, X, Gift, Calendar, Shield, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';

const ContactPage = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    company: '',
    phone: '',
    industry: '',
    message: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [emailStatus, setEmailStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Check for enterprise plan redirect
  useEffect(() => {
    const plan = searchParams.get('plan');
    if (plan === 'enterprise') {
      setFormData(prev => ({
        ...prev,
        message: 'I am interested in the Enterprise+ plan. Please contact me to discuss custom pricing and features.'
      }));
    }
  }, [searchParams]);

  // Debounced email validation
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (formData.email && formData.email.includes('@')) {
        setCheckingEmail(true);
        try {
          const response = await fetch(`${API_URL}/api/auth/validate-email`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: formData.email })
          });
          const result = await response.json();
          // For contact form, we don't care if email is already registered
          if (result.message === 'Email already registered') {
            setEmailStatus({ valid: true, message: 'Valid email' });
          } else {
            setEmailStatus(result);
          }
        } catch (err) {
          setEmailStatus({ valid: false, message: 'Error validating email' });
        } finally {
          setCheckingEmail(false);
        }
      } else {
        setEmailStatus(null);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.email, API_URL]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate work email
    if (!emailStatus?.valid) {
      setError(emailStatus?.message || 'Please enter a valid work email');
      return;
    }

    if (!formData.fullName.trim()) {
      setError('Please enter your full name');
      return;
    }

    setLoading(true);

    try {
      // Submit contact form to backend
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: formData.fullName,
          email: formData.email,
          company: formData.company,
          phone: formData.phone,
          industry: formData.industry,
          message: formData.message
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to submit form');
      }

      setSuccess('Thank you for reaching out! Our team will contact you within 24 hours.');
      setFormData({
        fullName: '',
        email: '',
        company: '',
        phone: '',
        industry: '',
        message: ''
      });
      setEmailStatus(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: "Email",
      detail: "hello@axiosera.com",
      action: "mailto:hello@axiosera.com"
    },
    {
      icon: Phone,
      title: "Phone",
      detail: "+31 6 29018613",
      action: "tel:+31629018613"
    },
    {
      icon: MapPin,
      title: "Office",
      detail: "Drielandendreef 42, 3845 CA, Harderwijk, The Netherlands",
      action: null
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0F1C]">
      <Header />
      
      {/* Hero */}
      <section className="pt-40 pb-16 px-6 bg-gradient-to-b from-[#0A0F1C] to-[#111827]">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-[#2563EB]/10 border border-[#2563EB]/30 rounded-full px-6 py-2 mb-6">
            <Gift className="w-4 h-4 text-[#2563EB]" />
            <span className="text-[#2563EB] font-medium text-sm">14-Day Free Trial Available</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-[#F8FAFC] mb-6">
            Let's
            <span className="bg-gradient-to-r from-[#2563EB] to-[#3B82F6] bg-clip-text text-transparent"> Talk</span>
          </h1>
          <p className="text-xl text-[#94A3B8] max-w-3xl mx-auto">
            Ready to transform your pricing? Have questions? We're here to help.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-6 bg-[#0A0F1C]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-2xl p-8">
              <h2 className="text-3xl font-bold text-[#F8FAFC] mb-2">Get Started</h2>
              <p className="text-[#94A3B8] mb-6">Fill out the form and our team will reach out within 24 hours.</p>

              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6 flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              {success && (
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-6 flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <p className="text-green-400 text-sm">{success}</p>
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Full Name */}
                <div>
                  <label className="block text-sm font-medium text-[#F8FAFC] mb-2">Full Name *</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
                    <input
                      type="text"
                      name="fullName"
                      required
                      value={formData.fullName}
                      onChange={handleChange}
                      className="w-full bg-[#0A0F1C] border border-[#2563EB]/30 rounded-lg py-3 pl-10 pr-4 text-[#F8FAFC] placeholder-[#94A3B8] focus:outline-none focus:border-[#2563EB]"
                      placeholder="John Doe"
                    />
                  </div>
                </div>

                {/* Work Email */}
                <div>
                  <label className="block text-sm font-medium text-[#F8FAFC] mb-2">Work Email *</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full bg-[#0A0F1C] border rounded-lg py-3 pl-10 pr-10 text-[#F8FAFC] placeholder-[#94A3B8] focus:outline-none ${
                        emailStatus === null ? 'border-[#2563EB]/30 focus:border-[#2563EB]' :
                        emailStatus.valid ? 'border-green-500' : 'border-red-500'
                      }`}
                      placeholder="you@company.com"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {checkingEmail && <Loader2 className="w-5 h-5 text-[#94A3B8] animate-spin" />}
                      {!checkingEmail && emailStatus?.valid && <Check className="w-5 h-5 text-green-400" />}
                      {!checkingEmail && emailStatus && !emailStatus.valid && <X className="w-5 h-5 text-red-400" />}
                    </div>
                  </div>
                  {emailStatus && !emailStatus.valid && (
                    <p className="text-red-400 text-xs mt-1">{emailStatus.message}</p>
                  )}
                  <p className="text-[#94A3B8] text-xs mt-1">Free email providers (Gmail, Yahoo, etc.) are not allowed</p>
                </div>

                {/* Company & Phone Row */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#F8FAFC] mb-2">Company *</label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
                      <input
                        type="text"
                        name="company"
                        required
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full bg-[#0A0F1C] border border-[#2563EB]/30 rounded-lg py-3 pl-10 pr-4 text-[#F8FAFC] placeholder-[#94A3B8] focus:outline-none focus:border-[#2563EB]"
                        placeholder="Acme Inc."
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-[#F8FAFC] mb-2">Phone</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full bg-[#0A0F1C] border border-[#2563EB]/30 rounded-lg py-3 pl-10 pr-4 text-[#F8FAFC] placeholder-[#94A3B8] focus:outline-none focus:border-[#2563EB]"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                  </div>
                </div>

                {/* Industry */}
                <div>
                  <label className="block text-sm font-medium text-[#F8FAFC] mb-2">Industry</label>
                  <select
                    name="industry"
                    value={formData.industry}
                    onChange={handleChange}
                    className="w-full bg-[#0A0F1C] border border-[#2563EB]/30 rounded-lg py-3 px-4 text-[#F8FAFC] focus:outline-none focus:border-[#2563EB]"
                  >
                    <option value="">Select your industry</option>
                    <option value="retail">Retail & E-commerce</option>
                    <option value="healthcare">Healthcare</option>
                    <option value="tech">Tech & SaaS</option>
                    <option value="manufacturing">Manufacturing</option>
                    <option value="services">Professional Services</option>
                    <option value="commodities">Commodities</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-[#F8FAFC] mb-2">Message</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    className="w-full bg-[#0A0F1C] border border-[#2563EB]/30 rounded-lg py-3 px-4 text-[#F8FAFC] placeholder-[#94A3B8] focus:outline-none focus:border-[#2563EB] resize-none"
                    placeholder="Tell us about your pricing challenges or questions..."
                  />
                </div>

                <Button 
                  type="submit" 
                  disabled={loading || !emailStatus?.valid}
                  className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-8 py-6 text-lg font-semibold rounded-lg w-full disabled:opacity-50"
                >
                  {loading ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Sending...</>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>

                <p className="text-center text-[#94A3B8] text-sm">
                  Want to try first?{' '}
                  <Link to="/coming-soon" className="text-[#2563EB] hover:underline font-medium">
                    Get started free
                  </Link>
                </p>
              </form>
            </div>

            {/* Contact Info */}
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl font-bold text-[#F8FAFC] mb-2">Get in Touch</h2>
                <p className="text-[#94A3B8]">
                  Our team typically responds within 24 hours. For urgent inquiries, please call us directly.
                </p>
              </div>

              <div className="space-y-4">
                {contactInfo.map((info, index) => {
                  const Icon = info.icon;
                  return (
                    <div key={index} className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-xl p-5">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-[#2563EB]/10 rounded-lg flex items-center justify-center">
                          <Icon className="w-6 h-6 text-[#2563EB]" />
                        </div>
                        <div>
                          <p className="text-[#94A3B8] text-sm mb-1">{info.title}</p>
                          {info.action ? (
                            <a href={info.action} className="text-[#F8FAFC] font-semibold hover:text-[#2563EB] transition-colors">
                              {info.detail}
                            </a>
                          ) : (
                            <p className="text-[#F8FAFC] font-semibold">{info.detail}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Free Pricing Check Card */}
              <div className="bg-gradient-to-br from-[#2563EB]/20 to-[#111827] border border-[#2563EB]/40 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <Shield className="w-6 h-6 text-[#2563EB]" />
                  <h3 className="text-xl font-bold text-[#F8FAFC]">Free Pricing Check</h3>
                </div>
                <p className="text-[#94A3B8] mb-4">
                  See PriceStrike in action. Try our free tool with your own data.
                </p>
                <Link to="/pricing-check">
                  <Button className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white w-full py-4 font-semibold">
                    <ArrowRight className="w-5 h-5 mr-2" />
                    Run Free Check
                  </Button>
                </Link>
              </div>

              {/* Quick Links */}
              <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-xl p-6">
                <h3 className="text-xl font-bold text-[#F8FAFC] mb-4">Quick Links</h3>
                <div className="grid grid-cols-2 gap-3">
                  <Link to="/pricing">
                    <Button variant="outline" className="border-[#2563EB]/50 text-[#F8FAFC] hover:bg-[#2563EB]/10 w-full py-3">
                      View Pricing
                    </Button>
                  </Link>
                  <Link to="/features">
                    <Button variant="outline" className="border-[#2563EB]/50 text-[#F8FAFC] hover:bg-[#2563EB]/10 w-full py-3">
                      All Features
                    </Button>
                  </Link>
                  <Link to="/coming-soon">
                    <Button variant="outline" className="border-[#2563EB]/50 text-[#F8FAFC] hover:bg-[#2563EB]/10 w-full py-3">
                      Get Started
                    </Button>
                  </Link>
                  <Link to="/industries">
                    <Button variant="outline" className="border-[#2563EB]/50 text-[#F8FAFC] hover:bg-[#2563EB]/10 w-full py-3">
                      Industries
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
